/**
 * CorrespondenceTab - Sprint Y Block E
 * 
 * Tab component to embed correspondence timeline in entity detail pages.
 * Used in:
 * - MissionDetail (linked_mission_id)
 * - AuthorizationDetail (linked_authorization_id)
 * - PilotDetail (linked_pilot_id)
 * 
 * HITL: Visual distinction between PROPOSED and CONFIRMED links.
 */

import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { RefreshCw, Mail } from "lucide-react";
import { CorrespondenceTimeline } from "./CorrespondenceTimeline";
import { CorrespondenceCard } from "./CorrespondenceCard";
import { CorrespondenceDetailModal } from "./CorrespondenceDetailModal";
import { Button } from "../ui/button";
import { toast } from "sonner";

export const CorrespondenceTab = ({
  entityType, // 'authorization' | 'mission' | 'pilot'
  entityId,
  includeProposed = true,
  compact = false,
  maxItems = 10,
}) => {
  const { translate: t, language } = useLanguage();
  const [correspondence, setCorrespondence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  const fetchCorrespondence = useCallback(async () => {
    if (!entityId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("include_proposed", includeProposed);
      
      const response = await api.get(
        `/correspondence/entity/${entityType}/${entityId}?${params.toString()}`
      );
      setCorrespondence(response.data);
    } catch (error) {
      console.error("Failed to fetch correspondence:", error);
      if (error.response?.status !== 403) {
        toast.error(t("messages.fetchError"));
      }
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, includeProposed, t]);
  
  useEffect(() => {
    fetchCorrespondence();
  }, [fetchCorrespondence]);
  
  const handleViewDetails = (item) => {
    setSelectedItem(item);
    setModalOpen(true);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid={`correspondence-tab-${entityType}-loading`}>
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  if (correspondence.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center" data-testid={`correspondence-tab-${entityType}-empty`}>
        <Mail className="w-10 h-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm text-muted-foreground">{t("correspondence.noCorrespondence")}</p>
        <p className="text-xs text-muted-foreground/70 mt-1">{t("correspondence.noCorrespondenceDesc")}</p>
      </div>
    );
  }
  
  if (compact) {
    // Compact view for sidebars - show just cards
    return (
      <div className="space-y-2" data-testid={`correspondence-tab-${entityType}-compact`}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium">{t("correspondence.tabTitle")}</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchCorrespondence}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
        {correspondence.slice(0, maxItems).map((item) => (
          <CorrespondenceCard
            key={item.id}
            correspondence={item}
            onViewDetails={handleViewDetails}
            compact
          />
        ))}
        {correspondence.length > maxItems && (
          <p className="text-xs text-muted-foreground text-center">
            +{correspondence.length - maxItems} {language === "cs" ? "dalších" : "more"}
          </p>
        )}
        
        <CorrespondenceDetailModal
          correspondence={selectedItem}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      </div>
    );
  }
  
  // Full timeline view
  return (
    <div data-testid={`correspondence-tab-${entityType}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-medium">{t("correspondence.tabTitle")}</h3>
          <p className="text-xs text-muted-foreground">{t("correspondence.tabDescription")}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchCorrespondence}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          {t("actions.refresh")}
        </Button>
      </div>
      
      <CorrespondenceTimeline
        correspondence={correspondence}
        onViewDetails={handleViewDetails}
        maxItems={maxItems}
        showLoadMore
      />
      
      <CorrespondenceDetailModal
        correspondence={selectedItem}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};

export default CorrespondenceTab;
